import React, { useEffect, useState } from "react";
import { MainCard } from "../commons";
import rubriqueHero from "../../assets/herobg/rubrique-hero.webp";
import { getAllArticlePublished } from "../../services/article.service";
import { getAllRubriques } from "../../services/rubrique.service";
import { useNavigate } from "react-router-dom";
import { useDateFormatter } from "../../hooks";

const REACT_APP_BASE_URL = process.env.REACT_APP_BASE_URL;

const ArticleByRub = () => {
    const [groupedArticles, setGroupedArticles] = useState({});
    const [rubriqueOrder, setRubriqueOrder] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const formatDate = useDateFormatter();

    // Fonction pour formater les rubriques en chaîne de caractères
    const formatRubriques = (rubriques) => {
        if (!rubriques || !Array.isArray(rubriques) || rubriques.length === 0) {
            return '';
        }

        return rubriques.map(rubrique => rubrique.name_rub).join(', ');
    };

    // Fonction pour charger les articles et rubriques
    useEffect(() => {
        const fetchArticlesAndRubriques = async () => {
            try {
                setLoading(true);

                // Appel des APIs
                const [articlesResponse, rubriquesResponse] = await Promise.all([
                    getAllArticlePublished(1, 100), // Limite de 100 articles par page
                    getAllRubriques(),
                ]);

                const articles = articlesResponse.data;
                const rubriques = rubriquesResponse.data;

                // Créer un objet pour stocker les articles regroupés par rubrique
                const grouped = {};

                // Pour chaque article, vérifier ses rubriques et l'ajouter aux groupes correspondants
                articles.forEach(article => {
                    // Si l'article a un tableau de rubriques, l'ajouter à chaque rubrique
                    if (article.rubriques && Array.isArray(article.rubriques) && article.rubriques.length > 0) {
                        article.rubriques.forEach(rubrique => {
                            const rubriqueName = rubrique.name_rub;
                            if (!grouped[rubriqueName]) {
                                grouped[rubriqueName] = [];
                            }
                            grouped[rubriqueName].push(article);
                        });
                    }
                    // Compatibilité avec l'ancien format utilisant name_rub
                    else if (article.name_rub) {
                        const rubriqueName = article.name_rub;
                        if (!grouped[rubriqueName]) {
                            grouped[rubriqueName] = [];
                        }
                        grouped[rubriqueName].push(article);
                    }
                });

                setGroupedArticles(grouped);

                // Ordre des rubriques basé sur la réponse de l'API
                setRubriqueOrder(
                    rubriques.map((rubrique) => rubrique.name_rub)
                );
            } catch (err) {
                console.error("Erreur lors du chargement des données :", err);
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        fetchArticlesAndRubriques();
    }, []);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64 mt-12">
                <div className="w-12 h-12 border-4 border-greenlogo border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center text-red-600 py-8 mt-12">
                Erreur lors du chargement des données : {error.message}
            </div>
        );
    }

    // Fonction pour gérer le clic sur un article
    const handleArticleClick = (slug) => {
        navigate(`/article/${slug}`);
    };

    return (
        <div>
            <div className="flex-grow px-6 sm:px-10 lg:px-16 max-w-7xl mx-auto w-full mb-12 mt-12">
                {rubriqueOrder.map((rubrique, index) => (
                    groupedArticles[rubrique] && (
                        <section key={index} className="mb-12">
                            <h2 className="text-3xl sm:text-4xl font-semibold text-greenprimary flex items-center mb-8">
                                <span className="w-10 sm:w-16 h-1.5 sm:h-2.5 mt-2 sm:mt-3 bg-greenprimary mr-2"></span>
                                {rubrique}
                            </h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {groupedArticles[rubrique].map((article, articleIndex) => (
                                    <div
                                        key={`${article.id_art}-${articleIndex}`}
                                        className="cursor-pointer"
                                        onClick={() => handleArticleClick(article.slug)}
                                    >
                                        <MainCard
                                            id={article.id_art}
                                            cover={
                                                article.photo_art
                                                    ? `${REACT_APP_BASE_URL}/${article.photo_art}`
                                                    : rubriqueHero
                                            }
                                            rubrique={article.rubriques && Array.isArray(article.rubriques)
                                                ? formatRubriques(article.rubriques)
                                                : article.name_rub}
                                            title={article.title_art.fr}
                                            description={article.key_words_art.fr.join(", ")}
                                            date={formatDate(article.date_pub_art)}
                                            auteur={`${article.author.title_aut} ${article.author.first_name} ${article.author.last_name}`}
                                        />
                                    </div>
                                ))}
                            </div>
                        </section>
                    )
                ))}
            </div>
        </div>
    );
};

export default ArticleByRub;